// Spreadsheets the app hands out: a fill-in template for one person, and a full report of everything.

import { writeXlsx, dateToSerial } from './xlsx.js';
import { monthLabel, addMonths, monthOf } from './calendar.js';
import { r2 } from './money.js';
import { LOG_HEADERS, LOG_TYPES } from './sheetImport.js';
import { versionAt } from './state.js';
import { fmt } from './money.js';
import { jovemProjection } from './projection.js';

const items = (list) => (Array.isArray(list) && list.length ? r2(list.reduce((a, x) => a + (Number(x.amount) || 0), 0)) : null);

// The fill-in spreadsheet for one person: a single table where each row is money that came in or an extra expense.
// No expected values: only what really happened. What the app already has for them is filled in, so the file can
// go back and forth.
export function buildTemplate(state, model, personId, { blankRows = 60 } = {}) {
  const p = state.people.find((x) => x.id === personId);
  if (!p) throw new Error('Unknown person');
  const entries = [];
  for (const [month, m] of Object.entries(state.months || {}).sort()) {
    const r = m.people?.[personId];
    if (!r) continue;
    const e = model.byKey[month]?.people.find((x) => x.personId === personId);
    if (typeof r.actualNet === 'number') entries.push({ date: e?.payday || `${month}-28`, type: 'salary', description: 'Salary', amount: r.actualNet });
    for (const it of r.extraIncome || []) entries.push({ date: `${month}-01`, type: 'income', description: it.label || '', amount: it.amount });
    for (const it of r.extraSpending || []) if (it.amount > 0) entries.push({ date: `${month}-01`, type: 'expense', description: it.label || '', amount: it.amount });
  }
  entries.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const head = 4; // header on row 4 (index 3)
  const total = Math.max(entries.length + blankRows, blankRows);
  const band = (i) => (i % 2 ? 'B' : 'A');
  const rows = [
    [{ v: `${p.name}'s money`, style: 'title' }],
    [{ v: `Add a row whenever money comes in (your salary, or anything else) or you pay for something outside your usual ${versionAt(p.allowance, model.current) ? `${fmt(versionAt(p.allowance, model.current).amount)} ` : ''}monthly spending. Leave the rest to the app.`, style: 'muted' }],
    [],
    LOG_HEADERS.map((h) => ({ v: h === 'Amount' ? 'Amount' : h, style: 'header' })),
  ];
  for (let i = 0; i < total; i++) {
    const e = entries[i];
    const b = band(i);
    rows.push(e
      ? [{ v: dateToSerial(e.date), style: `date${b}` }, { v: LOG_TYPES[e.type], style: `text${b}` }, { v: e.description, style: `text${b}` }, { v: e.amount, style: `money${b}` }]
      : [{ v: null, style: `date${b}` }, { v: null, style: `text${b}` }, { v: null, style: `text${b}` }, { v: null, style: `money${b}` }]);
  }
  const first = head + 1;
  const last = head + total;
  const howTo = [
    [{ v: 'How it works', style: 'title' }],
    [],
    ...[
      `Each row is one movement of money. Fill in four things:`,
      `• Date: the day it happened (e.g. 31/10/2026).`,
      `• Type: pick from the list —`,
      `     ${LOG_TYPES.salary}: your pay, as it arrived in the bank.`,
      `     ${LOG_TYPES.income}: anything else that came in (a gift, something you sold, a refund…).`,
      `     ${LOG_TYPES.expense}: something you paid that's outside your normal monthly spending (car repair, a trip, the dentist…).`,
      `• Description: what it was (optional).`,
      `• Amount: how much, in euros (1234,56 or 1234.56).`,
      '',
      `Your normal monthly spending${versionAt(p.allowance, model.current) ? ` (${fmt(versionAt(p.allowance, model.current).amount)})` : ''} and the shared bills (rent, electricity…) are already counted in the app, so don't add them.`,
      'Add more rows at the bottom whenever you need; the order doesn\'t matter.',
      '',
      'To send it to the app: in the app, Settings → Spreadsheets → Upload, or keep it in Google Sheets and link it there so the app reads it by itself.',
    ].map((t) => [{ v: t, style: 'wrap' }]),
  ];
  return writeXlsx([
    {
      name: p.name,
      rows,
      columns: [{ width: 13 }, { width: 16 }, { width: 44 }, { width: 14 }],
      freeze: { row: head },
      rowHeights: { 1: 32, 3: 22 },
      merges: ['A1:D1', 'A2:D2'],
      table: { name: `${p.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]/g, '') || 'Person'}Money`, ref: `A${head}:D${last}`, columns: LOG_HEADERS, style: 'TableStyleMedium2' },
      validations: [
        { type: 'list', formula1: `"${Object.values(LOG_TYPES).join(',')}"`, sqref: `B${first}:B${last + 500}`, error: `Pick ${Object.values(LOG_TYPES).join(', ')}`, prompt: `${LOG_TYPES.salary}, ${LOG_TYPES.income} or ${LOG_TYPES.expense}` },
        { type: 'date', operator: 'greaterThan', formula1: '36526', sqref: `A${first}:A${last + 500}`, error: 'Type a date, like 31/10/2026' },
        { type: 'decimal', operator: 'greaterThanOrEqual', formula1: '0', sqref: `D${first}:D${last + 500}`, error: 'Type an amount, like 1234,56' },
      ],
    },
    { name: 'How it works', rows: howTo, columns: [{ width: 110 }] },
    { name: '_meta', hidden: true, rows: [[{ v: 'app' }, { v: 'joint-savings' }], [{ v: 'personId' }, { v: personId }], [{ v: 'personName' }, { v: p.name }], [{ v: 'version' }, { v: 2 }]] },
  ]);
}

// Everything, for looking at in Excel
export function buildReport(state, model) {
  const people = state.people;
  const nameOf = (id) => (id === 'joint' ? 'Joint' : people.find((p) => p.id === id)?.name || '?');
  const months = model.months.filter((m) => m.key <= addMonths(model.current, 12));

  const summaryHead = ['Month', 'Status', ...people.flatMap((p) => [`${p.name}: take-home expected`, `${p.name}: take-home actual`, `${p.name}: to savings`]), 'Joint expenses (budget)', 'Expenses over budget', 'Savings expected', 'Savings', 'Balance'];
  const summary = [summaryHead.map((h) => ({ v: h, style: 'header' }))];
  for (const m of months) {
    const row = [{ v: m.key, style: 'monthKey' }, { v: m.isFuture ? 'projected' : m.status }];
    for (const p of people) {
      const e = m.people.find((x) => x.personId === p.id);
      row.push(e ? { v: e.expected.net, style: 'money' } : null, e ? { v: e.actual.net, style: 'money' } : null, e ? { v: e.actual.contribution, style: 'money' } : null);
    }
    row.push({ v: m.jointBudget, style: 'money' }, { v: m.variance, style: 'money' }, { v: m.expectedSavings, style: 'money' }, { v: m.savings, style: 'moneyBold' }, { v: m.balance, style: 'money' });
    summary.push(row);
  }

  const payHead = ['Month', 'Person', 'Payday', 'Status', 'Gross taxable', 'Social security', 'IRS withheld', 'Take-home expected', 'Take-home received', 'Reimbursements in it', 'Meal card', 'IRS refund', 'Other income', 'Personal budget', 'Own expenses', 'Share of joint expenses', 'Spent beyond budget', 'To savings (expected)', 'To savings', 'Notes'];
  const pay = [payHead.map((h) => ({ v: h, style: 'header' }))];
  for (const m of months) {
    for (const e of m.people) {
      pay.push([
        { v: m.key, style: 'monthKey' }, e.name, e.payday, m.isFuture ? 'projected' : e.status,
        { v: e.slip.taxable, style: 'money' }, { v: e.slip.ss, style: 'money' }, { v: e.slip.irs, style: 'money' },
        { v: e.expected.net, style: 'money' }, { v: e.actual.net, style: 'money' }, { v: e.actual.reimbursed, style: 'money' }, { v: e.actual.meal, style: 'money' },
        { v: e.actual.refund, style: 'money' }, { v: e.actual.extraIncome, style: 'money' }, { v: e.expected.personal, style: 'money' },
        { v: e.expected.own, style: 'money' }, { v: e.expected.jointShare, style: 'money' }, { v: e.actual.extraSpending, style: 'money' },
        { v: e.expected.contribution, style: 'money' }, { v: e.actual.contribution, style: 'moneyBold' }, e.review.note || null,
      ]);
    }
  }

  const exHead = ['Expense', 'Category', 'Paid by', 'How often', 'From', 'Amount', 'Ends'];
  const ex = [exHead.map((h) => ({ v: h, style: 'header' }))];
  for (const e of state.expenses) {
    for (const v of e.versions) {
      ex.push([e.name, e.category, nameOf(e.owner), e.every === 'month' ? 'Monthly' : e.every === 'year' ? `Yearly (month ${e.dueMonth}${e.spread ? ', spread' : ''})` : 'Once',
        { v: v.from, style: 'monthKey' }, { v: v.amount, style: 'money' }, e.end || null]);
    }
  }

  const irsHead = ['Person', 'Year', 'IRS Jovem year', 'Exempt %', 'Normal month take-home', 'Taxable income', 'Social security', 'IRS withheld', 'Estimated IRS due', 'Estimated refund (next year)', 'Figures'];
  const irs = [irsHead.map((h) => ({ v: h, style: 'header' }))];
  for (const p of people) {
    const proj = jovemProjection(state, p.id);
    for (const r of proj.rows) {
      irs.push([p.name, r.year, r.jovem.eligible ? r.jovem.index : '—', { v: r.jovem.eligible ? r.jovem.pct / 100 : 0, style: 'pct' },
        r.normal ? { v: r.normal.net, style: 'money' } : null, { v: r.annual.gross, style: 'money' }, { v: r.annual.ss, style: 'money' },
        { v: r.annual.irs, style: 'money' }, r.settlement ? { v: r.settlement.tax, style: 'money' } : null,
        r.settlement ? { v: r.settlement.refund, style: 'money' } : null, r.estimatedTax ? `estimate (${r.taxYear} rules)` : `${r.year} rules`]);
    }
  }

  const sav = [['Date', 'Description', 'Amount'].map((h) => ({ v: h, style: 'header' }))];
  sav.push(['', 'Starting balance', { v: state.savings.opening || 0, style: 'money' }]);
  for (const t of state.savings.transactions) sav.push([t.date, t.label, { v: t.amount, style: 'money' }]);

  const wide = (n, w = 14) => Array.from({ length: n }, () => ({ width: w }));
  return writeXlsx([
    { name: 'Summary', rows: summary, columns: [{ width: 10 }, { width: 11 }, ...wide(summaryHead.length - 2, 16)], freeze: { row: 1, col: 1 } },
    { name: 'Paydays', rows: pay, columns: [{ width: 10 }, { width: 12 }, { width: 11 }, { width: 11 }, ...wide(payHead.length - 5), { width: 30 }], freeze: { row: 1, col: 2 } },
    { name: 'Expenses', rows: ex, columns: [{ width: 24 }, { width: 14 }, { width: 12 }, { width: 22 }, { width: 10 }, { width: 12 }, { width: 10 }], freeze: { row: 1 } },
    { name: 'IRS by year', rows: irs, columns: [{ width: 12 }, { width: 8 }, { width: 10 }, { width: 10 }, ...wide(6, 16), { width: 20 }], freeze: { row: 1 } },
    { name: 'Savings', rows: sav, columns: [{ width: 12 }, { width: 40 }, { width: 14 }], freeze: { row: 1 } },
    // Marks the file as the report, so uploading it by mistake is refused instead of read as figures
    { name: '_meta', hidden: true, rows: [[{ v: 'app' }, { v: 'joint-savings' }], [{ v: 'kind' }, { v: 'report' }]] },
  ]);
}

export const reportFileName = (today) => `joint-savings-${today}.xlsx`;
export const templateFileName = (name) => `joint-savings-${String(name).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-')}.xlsx`;
export { monthLabel };
